import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useToast } from '../../../hooks/use-toast';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import { Save, DollarSign, Tag, Plus, Trash2, ChevronDown, ChevronUp, Monitor, Wifi, MapPin } from 'lucide-react';
import {
  parseDeadlineParts,
  mergeDeadlineDateTime,
  DEFAULT_HUB_TIME_ZONE,
} from '../../../lib/deadlineDateTime';
import DurationScheduleEditor from '../DurationScheduleEditor';
import CollapsibleSection from '../CollapsibleSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Program fields this tab edits — merge onto fresh server rows so image / copy / hub-only fields are never wiped. */
const PRICING_PROGRAM_FIELD_KEYS = [
  'visible', 'enable_online', 'enable_offline', 'enable_in_person',
  'show_pricing_on_card', 'show_tiers_on_card',
  'duration', 'weekends_only', 'session_days',
  'price_aed', 'price_inr', 'price_usd',
  'early_bird_price_aed', 'early_bird_price_inr', 'early_bird_price_usd',
  'early_bird_date', 'early_bird_text',
  'offer_price_aed', 'offer_price_inr', 'offer_price_usd', 'offer_text',
  'pay_as_you_wish', 'pay_as_you_wish_minimum_inr', 'pay_as_you_wish_suggested_inr',
];

const TIER_PRICING_FIELD_KEYS = [
  'label', 'duration_value', 'duration_unit', 'duration', 'weekends_only', 'session_days', 'visible_on_website',
  'price_aed', 'price_inr', 'price_usd',
  'early_bird_price_aed', 'early_bird_price_inr', 'early_bird_price_usd',
  'early_bird_date', 'early_bird_text',
  'offer_price_aed', 'offer_price_inr', 'offer_price_usd', 'offer_text',
];

const PRICING_SESSION_FIELD_KEYS = [
  'visible', 'enable_online', 'enable_offline', 'enable_in_person', 'show_pricing',
  'price_aed', 'price_inr', 'price_usd',
  'early_bird_price_aed', 'early_bird_price_inr', 'early_bird_price_usd',
  'early_bird_date', 'early_bird_text',
  'offer_price_aed', 'offer_price_inr', 'offer_price_usd', 'offer_text',
  'pay_as_you_wish', 'pay_as_you_wish_minimum_inr', 'pay_as_you_wish_suggested_inr',
];

function mergeDurationTiersForPricingSave(serverTiers = [], localTiers = []) {
  const st = Array.isArray(serverTiers) ? serverTiers : [];
  const lt = Array.isArray(localTiers) ? localTiers : [];
  return lt.map((loc, i) => {
    const base = st[i] || {};
    const merged = { ...base };
    for (const k of TIER_PRICING_FIELD_KEYS) {
      if (Object.prototype.hasOwnProperty.call(loc, k)) merged[k] = loc[k];
    }
    merged.start_date = base.start_date ?? loc.start_date ?? '';
    merged.end_date = base.end_date ?? loc.end_date ?? '';
    merged.weekends_only = loc.weekends_only ?? base.weekends_only ?? false;
    merged.session_days = loc.session_days ?? base.session_days ?? 0;
    merged.visible_on_website = loc.visible_on_website ?? base.visible_on_website ?? true;
    if (loc.duration !== undefined && loc.duration !== null && loc.duration !== '') {
      merged.duration = loc.duration;
    }
    return merged;
  });
}

function buildPricingProgramPutPayload(local, server) {
  const base = server && typeof server === 'object' ? { ...server } : { ...local };
  for (const k of PRICING_PROGRAM_FIELD_KEYS) {
    if (Object.prototype.hasOwnProperty.call(local, k)) base[k] = local[k];
  }
  base.duration_tiers = mergeDurationTiersForPricingSave(server?.duration_tiers, local?.duration_tiers);
  return base;
}

function buildPricingSessionPutPayload(local, server) {
  const base = server && typeof server === 'object' ? { ...server } : { ...local };
  for (const k of PRICING_SESSION_FIELD_KEYS) {
    if (Object.prototype.hasOwnProperty.call(local, k)) base[k] = local[k];
  }
  return base;
}

const DURATION_PRESETS = [
  ...Array.from({ length: 30 }, (_, i) => `${i + 1} Day${i > 0 ? 's' : ''}`),
  '1 Week', '2 Weeks', '3 Weeks', '4 Weeks',
  '1 Month', '2 Months', '3 Months', '4 Months', '5 Months', '6 Months',
  '7 Months', '8 Months', '9 Months', '10 Months', '11 Months', '12 Months',
  'Annual'
];

const Cell = ({ value, onChange, className = '' }) => {
  const [localVal, setLocalVal] = React.useState(String(value ?? 0));
  const [focused, setFocused] = React.useState(false);

  React.useEffect(() => {
    if (!focused) setLocalVal(String(value ?? 0));
  }, [value, focused]);

  return (
    <input type="text" inputMode="decimal" value={localVal}
      onChange={e => setLocalVal(e.target.value)}
      onFocus={e => { setFocused(true); if (e.target.value === '0') e.target.select(); }}
      onBlur={() => { setFocused(false); const num = localVal === '' ? 0 : parseFloat(localVal) || 0; onChange(num); }}
      className={`h-7 text-[11px] w-full text-center px-1 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37] ${className}`} />
  );
};

const DateCell = ({ value, onChange }) => (
  <input
    type="date"
    value={(value || '').slice(0, 10)}
    onChange={(e) => onChange(e.target.value)}
    className="h-7 text-[10px] w-full px-1 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
  />
);

const TimeCell = ({ value, onChange }) => (
  <input
    type="time"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    className="h-7 text-[10px] w-full px-1 border rounded-md focus:outline-none focus:ring-1 focus:ring-[#D4AF37]"
  />
);

const EarlyBirdDateTimeCells = ({ row, timeZone, onUpdate }) => {
  const parts = parseDeadlineParts(row.early_bird_date);
  const tz = timeZone || DEFAULT_HUB_TIME_ZONE;
  return (
    <>
      <td className="px-1 py-1 min-w-[108px]">
        <DateCell
          value={parts.date}
          onChange={(d) => onUpdate('early_bird_date', mergeDeadlineDateTime(d, parts.time, tz))}
        />
      </td>
      <td className="px-1 py-1 min-w-[88px]">
        <TimeCell
          value={parts.time}
          onChange={(t) => onUpdate('early_bird_date', mergeDeadlineDateTime(parts.date, t, tz))}
        />
      </td>
    </>
  );
};

const PricingOfferCells = ({ row, timeZone, onUpdate }) => (
  <>
    <td className="px-1 py-1"><Cell value={row.early_bird_price_aed || 0} onChange={v => onUpdate('early_bird_price_aed', v)} className="bg-amber-50/80" /></td>
    <td className="px-1 py-1"><Cell value={row.early_bird_price_inr || 0} onChange={v => onUpdate('early_bird_price_inr', v)} className="bg-amber-50/80" /></td>
    <td className="px-1 py-1"><Cell value={row.early_bird_price_usd || 0} onChange={v => onUpdate('early_bird_price_usd', v)} className="bg-amber-50/80" /></td>
    <EarlyBirdDateTimeCells row={row} timeZone={timeZone} onUpdate={onUpdate} />
    <td className="px-1 py-1"><Input value={row.early_bird_text || ''} onChange={e => onUpdate('early_bird_text', e.target.value)} placeholder="Early Bird" className="h-7 text-[10px]" /></td>
    <td className="px-1 py-1"><Cell value={row.offer_price_aed || 0} onChange={v => onUpdate('offer_price_aed', v)} /></td>
    <td className="px-1 py-1"><Cell value={row.offer_price_inr || 0} onChange={v => onUpdate('offer_price_inr', v)} /></td>
    <td className="px-1 py-1"><Cell value={row.offer_price_usd || 0} onChange={v => onUpdate('offer_price_usd', v)} /></td>
    <td className="px-1 py-1"><Input value={row.offer_text || ''} onChange={e => onUpdate('offer_text', e.target.value)} placeholder="Offer badge" className="h-7 text-[10px]" /></td>
  </>
);

const PricingHubTab = () => {
  const { toast } = useToast();
  const [programs, setPrograms] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [saving, setSaving] = useState(false);
  const [expandedPrograms, setExpandedPrograms] = useState({});

  const fetchData = useCallback(async () => {
    const [pRes, sRes] = await Promise.all([
      axios.get(`${API}/programs`),
      axios.get(`${API}/sessions`)
    ]);
    setPrograms(pRes.data || []);
    setSessions(sRes.data || []);
    const expanded = {};
    (pRes.data || []).forEach(p => { if (p.duration_tiers?.length > 0) expanded[p.id] = true; });
    setExpandedPrograms(expanded);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateProgram = (idx, field, value) => {
    setPrograms(prev => { const c = [...prev]; c[idx] = { ...c[idx], [field]: value }; return c; });
  };

  const updateTier = (pIdx, tIdx, field, value) => {
    setPrograms(prev => {
      const c = [...prev];
      const tiers = [...(c[pIdx].duration_tiers || [])];
      tiers[tIdx] = { ...tiers[tIdx], [field]: value };
      c[pIdx] = { ...c[pIdx], duration_tiers: tiers };
      return c;
    });
  };

  const addTier = (pIdx) => {
    setPrograms(prev => {
      const c = [...prev];
      const tiers = [...(c[pIdx].duration_tiers || [])];
      tiers.push({ label: '1 Month', duration_value: 1, duration_unit: 'month', visible_on_website: true, price_aed: 0, price_inr: 0, price_usd: 0, early_bird_price_aed: 0, early_bird_price_inr: 0, early_bird_price_usd: 0, early_bird_date: '', early_bird_text: '', offer_price_aed: 0, offer_price_inr: 0, offer_price_usd: 0, offer_text: '', start_date: '', end_date: '' });
      c[pIdx] = { ...c[pIdx], duration_tiers: tiers };
      setExpandedPrograms(e => ({ ...e, [c[pIdx].id]: true }));
      return c;
    });
  };

  const removeTier = (pIdx, tIdx) => {
    setPrograms(prev => {
      const c = [...prev];
      const tiers = [...(c[pIdx].duration_tiers || [])];
      tiers.splice(tIdx, 1);
      c[pIdx] = { ...c[pIdx], duration_tiers: tiers };
      return c;
    });
  };

  const updateSession = (idx, field, value) => {
    setSessions(prev => { const c = [...prev]; c[idx] = { ...c[idx], [field]: value }; return c; });
  };

  const saveAll = async () => {
    setSaving(true);
    try {
      const [freshProgRes, freshSessRes] = await Promise.all([
        axios.get(`${API}/programs`),
        axios.get(`${API}/sessions`),
      ]);
      const progById = new Map((freshProgRes.data || []).map(pr => [pr.id, pr]));
      const sessById = new Map((freshSessRes.data || []).map(se => [se.id, se]));
      const validPrograms = programs.filter(p => p.id && p.title);
      const validSessions = sessions.filter(s => s.id && s.title);
      const results = await Promise.allSettled([
        ...validPrograms.map((p) => {
          const server = progById.get(p.id);
          return axios.put(`${API}/programs/${p.id}`, buildPricingProgramPutPayload(p, server || p));
        }),
        ...validSessions.map((s) => {
          const server = sessById.get(s.id);
          return axios.put(`${API}/sessions/${s.id}`, buildPricingSessionPutPayload(s, server || s));
        }),
      ]);
      const failed = results.filter(r => r.status === 'rejected');
      if (failed.length > 0) {
        toast({ title: `Saved with ${failed.length} error(s)`, description: 'Some items could not be saved', variant: 'destructive' });
      } else {
        toast({ title: 'All pricing saved!' });
      }
    } catch (e) { toast({ title: 'Error saving', description: e.message, variant: 'destructive' }); }
    finally {
      try { await fetchData(); } catch (_) { /* refresh after save */ }
      setSaving(false);
    }
  };

  const toggleExpand = (id) => setExpandedPrograms(e => ({ ...e, [id]: !e[id] }));

  return (
    <div data-testid="pricing-hub-tab" className="w-full min-w-0 max-w-none">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><DollarSign size={18} className="text-[#D4AF37]" /> Pricing Hub</h2>
          <p className="text-xs text-gray-500 mt-1">Edit list, early bird, and offer prices. Use the <strong>Weekend workshop schedule</strong> panel below for Sat/Sun-only durations (e.g. 7 days). Per tier, toggle <strong>Web</strong> off to hide a duration on the public site (e.g. keep 3 Months in admin but show only 1 + 2 Months).</p>
        </div>
        <Button onClick={saveAll} disabled={saving} className="bg-[#D4AF37] hover:bg-[#b8962e]" data-testid="pricing-hub-save">
          <Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save All'}
        </Button>
      </div>

      {/* ===== WEEKEND WORKSHOP SCHEDULE (visible panel) ===== */}
      {(() => {
        const workshopRows = programs.filter(
          (p) => p.title && (p.is_upcoming || p.is_group_program),
        );
        if (workshopRows.length === 0) return null;
        return (
          <CollapsibleSection
            title="Weekend workshop schedule"
            subtitle={`${workshopRows.length} program${workshopRows.length === 1 ? '' : 's'} · session days & Wknd tag`}
            defaultOpen={false}
          >
            <div className="w-full -ml-5 pl-5" data-testid="pricing-workshop-schedule-panel">
              <p className="text-[11px] text-gray-600 mb-3">
                Enter <strong>session days</strong> (e.g. <strong>7</strong> for Jul&nbsp;31 + Aug&nbsp;1–2, 8–9, 15–16), then Save All.
                The card shows <em>7 Days</em> instead of the full start→end span (e.g. 17 days). Turn on <strong>Wknd tag</strong> only if every session is Sat/Sun.
                {workshopRows.some((p) => (p.duration_tiers || []).length > 0) && (
                  <> Programs with <strong>tiers</strong> need this set on each tier row below.</>
                )}
              </p>
              <div className="overflow-x-auto w-full border-t border-gray-200">
                <table className="w-full text-[11px] min-w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="text-left px-2 py-2 font-semibold text-gray-700 min-w-[220px]">Program</th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-700 min-w-[120px]">Tier</th>
                      <th className="text-left px-2 py-2 font-semibold text-gray-700 min-w-[320px]">Duration & session days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {workshopRows.flatMap((p) => {
                      const i = programs.findIndex((x) => x.id === p.id);
                      const tiers = p.duration_tiers || [];
                      if (tiers.length > 0) {
                        return tiers.map((t, ti) => (
                          <tr
                            key={`${p.id}-tier-${ti}`}
                            className="border-b hover:bg-amber-50/30"
                            data-testid={`pricing-workshop-schedule-${p.id}-${ti}`}
                          >
                            <td className="px-2 py-2 font-medium text-gray-900 align-top">
                              {ti === 0 ? p.title : ''}
                            </td>
                            <td className="px-2 py-2 text-gray-700 align-top">{t.label || `Tier ${ti + 1}`}</td>
                            <td className="px-2 py-2 align-top">
                              <DurationScheduleEditor
                                row={t}
                                compact
                                onUpdate={(field, value) => updateTier(i, ti, field, value)}
                              />
                            </td>
                          </tr>
                        ));
                      }
                      return (
                        <tr
                          key={p.id}
                          className="border-b hover:bg-amber-50/30"
                          data-testid={`pricing-workshop-schedule-${p.id}`}
                        >
                          <td className="px-2 py-2 font-medium text-gray-900 align-top">{p.title}</td>
                          <td className="px-2 py-2 text-gray-400 align-top">—</td>
                          <td className="px-2 py-2 align-top">
                            <DurationScheduleEditor
                              row={p}
                              compact
                              onUpdate={(field, value) => updateProgram(i, field, value)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </CollapsibleSection>
        );
      })()}

      {/* ===== ALL PROGRAMS ===== */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5"><Tag size={14} /> Programs</h3>
        <div className="overflow-x-auto w-full border-t border-gray-200">
          <table className="w-full text-[11px] min-w-full" data-testid="pricing-programs-table">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left px-2 py-2 font-semibold text-gray-700 min-w-[180px] sticky left-0 bg-gray-100 z-10">Name</th>
                <th className="px-1 py-2 font-semibold text-gray-600 w-12">Show</th>
                <th className="px-1 py-2 font-semibold text-blue-600 w-16" title="Enable Online mode"><div className="flex items-center justify-center gap-0.5"><Monitor size={11} /><span className="text-[9px]">Online</span></div></th>
                <th className="px-1 py-2 font-semibold text-teal-600 w-16" title="Enable Offline mode"><div className="flex items-center justify-center gap-0.5"><Wifi size={11} /><span className="text-[9px]">Offline</span></div></th>
                <th className="px-1 py-2 font-semibold text-orange-600 w-16" title="Enable In-Person mode"><div className="flex items-center justify-center gap-0.5"><MapPin size={11} /><span className="text-[9px]">In-Person</span></div></th>
                <th className="px-1 py-2 font-semibold text-purple-600 w-12" title="Show pricing on homepage & program page">Pricing</th>
                <th className="px-1 py-2 font-semibold text-purple-600 w-12" title="Show tier selector on homepage & program page">Tiers</th>
                <th className="px-1 py-2 font-semibold text-emerald-700 w-14" title="Client chooses INR amount at checkout">Pay wish</th>
                <th className="px-1 py-2 font-semibold text-emerald-600 min-w-[62px]" title="Minimum INR when pay-as-you-wish is on">Min ₹</th>
                <th className="px-1 py-2 font-semibold text-emerald-500 min-w-[62px]" title="Suggested INR (optional)">Sugg ₹</th>
                <th className="px-1 py-2 font-semibold text-blue-700 min-w-[70px]">AED</th>
                <th className="px-1 py-2 font-semibold text-green-700 min-w-[70px]">INR</th>
                <th className="px-1 py-2 font-semibold text-purple-700 min-w-[70px]">USD</th>
                <th className="px-1 py-2 font-semibold text-amber-700 min-w-[62px]" title="Early bird price until EB Date">EB AED</th>
                <th className="px-1 py-2 font-semibold text-amber-600 min-w-[62px]">EB INR</th>
                <th className="px-1 py-2 font-semibold text-amber-500 min-w-[62px]">EB USD</th>
                <th className="px-1 py-2 font-semibold text-amber-800 min-w-[108px]">EB Date</th>
                <th className="px-1 py-2 font-semibold text-amber-800 min-w-[88px]">EB Time</th>
                <th className="px-1 py-2 font-semibold text-amber-700 min-w-[72px]">EB Badge</th>
                <th className="px-1 py-2 font-semibold text-blue-500 min-w-[70px]" title="Regular offer after early bird ends">Offer AED</th>
                <th className="px-1 py-2 font-semibold text-green-500 min-w-[70px]">Offer INR</th>
                <th className="px-1 py-2 font-semibold text-purple-500 min-w-[70px]">Offer USD</th>
                <th className="px-1 py-2 font-semibold text-red-600 min-w-[90px]">Offer Badge</th>
                <th className="px-1 py-2 font-semibold text-amber-900 min-w-[130px]" title="Weekends-only workshop duration (expand tiers)">Schedule</th>
                <th className="px-1 py-2 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {programs.filter(p => !p.is_group_program).map((p, idx) => {
                const i = programs.findIndex(x => x.id === p.id);
                const hasTiers = (p.duration_tiers || []).length > 0;
                const isExpanded = expandedPrograms[p.id];
                return (
                  <React.Fragment key={p.id}>
                    <tr className={`border-b ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-yellow-50/30`} data-testid={`pricing-row-${p.id}`}>
                      <td className="px-2 py-1.5 sticky left-0 bg-inherit z-10">
                        <div className="flex items-center gap-1">
                          {hasTiers && (
                            <button onClick={() => toggleExpand(p.id)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                              {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                            </button>
                          )}
                          <div className="font-medium min-w-[160px]" title={p.title}>{p.title}</div>
                        </div>
                      </td>
                      <td className="px-1 py-1 text-center"><Switch checked={p.visible !== false} onCheckedChange={v => updateProgram(i, 'visible', v)} /></td>
                      <td className="px-1 py-1 text-center"><Switch checked={p.enable_online !== false} onCheckedChange={v => updateProgram(i, 'enable_online', v)} /></td>
                      <td className="px-1 py-1 text-center"><Switch checked={p.enable_offline !== false} onCheckedChange={v => updateProgram(i, 'enable_offline', v)} /></td>
                      <td className="px-1 py-1 text-center"><Switch checked={p.enable_in_person === true} onCheckedChange={v => updateProgram(i, 'enable_in_person', v)} /></td>
                      <td className="px-1 py-1 text-center"><Switch checked={p.show_pricing_on_card !== false} onCheckedChange={v => updateProgram(i, 'show_pricing_on_card', v)} /></td>
                      <td className="px-1 py-1 text-center"><Switch checked={p.show_tiers_on_card !== false} onCheckedChange={v => updateProgram(i, 'show_tiers_on_card', v)} /></td>
                      <td className="px-1 py-1 text-center"><Switch checked={!!p.pay_as_you_wish} onCheckedChange={v => updateProgram(i, 'pay_as_you_wish', v)} /></td>
                      <td className="px-1 py-1"><Cell value={p.pay_as_you_wish_minimum_inr ?? 450} onChange={v => updateProgram(i, 'pay_as_you_wish_minimum_inr', v)} className={p.pay_as_you_wish ? '' : 'opacity-40'} /></td>
                      <td className="px-1 py-1"><Cell value={p.pay_as_you_wish_suggested_inr ?? 0} onChange={v => updateProgram(i, 'pay_as_you_wish_suggested_inr', v)} className={p.pay_as_you_wish ? '' : 'opacity-40'} /></td>
                      {!hasTiers ? (
                        <>
                          <td className="px-1 py-1"><Cell value={p.price_aed} onChange={v => updateProgram(i, 'price_aed', v)} /></td>
                          <td className="px-1 py-1"><Cell value={p.price_inr} onChange={v => updateProgram(i, 'price_inr', v)} /></td>
                          <td className="px-1 py-1"><Cell value={p.price_usd} onChange={v => updateProgram(i, 'price_usd', v)} /></td>
                          <PricingOfferCells row={p} timeZone={p.time_zone || DEFAULT_HUB_TIME_ZONE} onUpdate={(field, value) => updateProgram(i, field, value)} />
                        </>
                      ) : (
                        <td colSpan={13} className="px-2 py-1 text-[10px] text-gray-400 italic cursor-pointer" onClick={() => toggleExpand(p.id)}>
                          {isExpanded ? `${(p.duration_tiers || []).length} tiers` : `${(p.duration_tiers || []).length} tiers — click to expand`}
                        </td>
                      )}
                      <td className="px-1 py-1">
                        <button onClick={() => addTier(i)} title="Add tier" className="text-[#D4AF37] hover:text-[#b8962e]"><Plus size={14} /></button>
                      </td>
                    </tr>
                    {hasTiers && isExpanded && (p.duration_tiers || []).map((t, ti) => (
                      <tr key={`${p.id}-t${ti}`} className="border-b bg-amber-50/40" data-testid={`pricing-tier-${p.id}-${ti}`}>
                        <td className="px-2 py-1 sticky left-0 bg-amber-50/40 z-10">
                          <div className="flex flex-col gap-1 ml-5 min-w-[140px]">
                            <select value={t.label || ''} onChange={e => updateTier(i, ti, 'label', e.target.value)}
                              className="border rounded px-1 py-1 text-[10px] bg-white w-[110px]">
                              {DURATION_PRESETS.map(d => <option key={d} value={d}>{d}</option>)}
                            </select>
                            <label
                              className="flex items-center gap-1.5 text-[9px] text-slate-700 cursor-pointer w-fit"
                              title="When off, this duration is hidden on the public website (homepage, program page, enroll). Still available in admin and payment links."
                            >
                              <Switch
                                checked={t.visible_on_website !== false}
                                onCheckedChange={(v) => updateTier(i, ti, 'visible_on_website', v)}
                                data-testid={`pricing-tier-web-${p.id}-${ti}`}
                              />
                              <span className="font-semibold uppercase tracking-wide">
                                {t.visible_on_website !== false ? 'On website' : 'Hidden'}
                              </span>
                            </label>
                          </div>
                        </td>
                        <td colSpan={9}></td>
                        <td className="px-1 py-1"><Cell value={t.price_aed} onChange={v => updateTier(i, ti, 'price_aed', v)} /></td>
                        <td className="px-1 py-1"><Cell value={t.price_inr} onChange={v => updateTier(i, ti, 'price_inr', v)} /></td>
                        <td className="px-1 py-1"><Cell value={t.price_usd} onChange={v => updateTier(i, ti, 'price_usd', v)} /></td>
                        <PricingOfferCells row={t} timeZone={p.time_zone || DEFAULT_HUB_TIME_ZONE} onUpdate={(field, value) => updateTier(i, ti, field, value)} />
                        <td className="px-1 py-1 align-top min-w-[120px]">
                          <DurationScheduleEditor row={t} compact onUpdate={(field, value) => updateTier(i, ti, field, value)} />
                        </td>
                        <td className="px-1 py-1">
                          <button onClick={() => removeTier(i, ti)} className="text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===== GROUP PROGRAMS ===== */}
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-emerald-700 mb-3 flex items-center gap-1.5"><Tag size={14} className="text-emerald-600" /> Group Programs</h3>
        {programs.filter(p => p.is_group_program).length === 0 ? (
          <p className="text-xs text-gray-400 italic pl-2">No group programs. Mark programs as "Group Program" in the Programs tab.</p>
        ) : (
          <div className="overflow-x-auto w-full border-t border-emerald-200">
            <table className="w-full text-[11px] min-w-full" data-testid="pricing-group-table">
              <thead>
                <tr className="bg-emerald-50 border-b">
                  <th className="text-left px-2 py-2 font-semibold text-gray-700 min-w-[180px] sticky left-0 bg-emerald-50 z-10">Name</th>
                  <th className="px-1 py-2 font-semibold text-gray-600 w-12">Show</th>
                  <th className="px-1 py-2 font-semibold text-emerald-700 w-14" title="Client chooses INR amount at checkout">Pay wish</th>
                  <th className="px-1 py-2 font-semibold text-emerald-600 min-w-[62px]" title="Minimum INR when pay-as-you-wish is on">Min ₹</th>
                  <th className="px-1 py-2 font-semibold text-emerald-500 min-w-[62px]" title="Suggested INR (optional)">Sugg ₹</th>
                  <th className="px-1 py-2 font-semibold text-blue-700 min-w-[70px]">AED</th>
                  <th className="px-1 py-2 font-semibold text-green-700 min-w-[70px]">INR</th>
                  <th className="px-1 py-2 font-semibold text-purple-700 min-w-[70px]">USD</th>
                  <th className="px-1 py-2 font-semibold text-amber-700 min-w-[62px]">EB AED</th>
                  <th className="px-1 py-2 font-semibold text-amber-600 min-w-[62px]">EB INR</th>
                  <th className="px-1 py-2 font-semibold text-amber-500 min-w-[62px]">EB USD</th>
                  <th className="px-1 py-2 font-semibold text-amber-800 min-w-[108px]">EB Date</th>
                  <th className="px-1 py-2 font-semibold text-amber-800 min-w-[88px]">EB Time</th>
                  <th className="px-1 py-2 font-semibold text-amber-700 min-w-[72px]">EB Badge</th>
                  <th className="px-1 py-2 font-semibold text-blue-500 min-w-[70px]">Offer AED</th>
                  <th className="px-1 py-2 font-semibold text-green-500 min-w-[70px]">Offer INR</th>
                  <th className="px-1 py-2 font-semibold text-purple-500 min-w-[70px]">Offer USD</th>
                  <th className="px-1 py-2 font-semibold text-red-600 min-w-[90px]">Offer Badge</th>
                  <th className="px-1 py-2 font-semibold text-amber-900 min-w-[130px]" title="Weekends-only workshop duration">Schedule</th>
                </tr>
              </thead>
              <tbody>
                {programs.filter(p => p.is_group_program).map((p, idx) => {
                  const i = programs.findIndex(x => x.id === p.id);
                  return (
                    <tr key={p.id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-emerald-50/30'} hover:bg-emerald-50/50`} data-testid={`pricing-group-${p.id}`}>
                      <td className="px-2 py-1.5 sticky left-0 bg-inherit z-10"><div className="font-medium min-w-[180px]" title={p.title}>{p.title}</div></td>
                      <td className="px-1 py-1 text-center"><Switch checked={p.visible !== false} onCheckedChange={v => updateProgram(i, 'visible', v)} /></td>
                      <td className="px-1 py-1 text-center"><Switch checked={!!p.pay_as_you_wish} onCheckedChange={v => updateProgram(i, 'pay_as_you_wish', v)} /></td>
                      <td className="px-1 py-1"><Cell value={p.pay_as_you_wish_minimum_inr ?? 450} onChange={v => updateProgram(i, 'pay_as_you_wish_minimum_inr', v)} className={p.pay_as_you_wish ? '' : 'opacity-40'} /></td>
                      <td className="px-1 py-1"><Cell value={p.pay_as_you_wish_suggested_inr ?? 0} onChange={v => updateProgram(i, 'pay_as_you_wish_suggested_inr', v)} className={p.pay_as_you_wish ? '' : 'opacity-40'} /></td>
                      <td className="px-1 py-1"><Cell value={p.price_aed} onChange={v => updateProgram(i, 'price_aed', v)} /></td>
                      <td className="px-1 py-1"><Cell value={p.price_inr} onChange={v => updateProgram(i, 'price_inr', v)} /></td>
                      <td className="px-1 py-1"><Cell value={p.price_usd} onChange={v => updateProgram(i, 'price_usd', v)} /></td>
                      <PricingOfferCells row={p} timeZone={p.time_zone || DEFAULT_HUB_TIME_ZONE} onUpdate={(field, value) => updateProgram(i, field, value)} />
                      <td className="px-1 py-1 align-top">
                        <DurationScheduleEditor row={p} compact onUpdate={(field, value) => updateProgram(i, field, value)} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== PERSONAL SESSIONS ===== */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-1.5"><Tag size={14} /> Personal Sessions</h3>
        <div className="overflow-x-auto w-full border-t border-gray-200">
          <table className="w-full text-[11px] min-w-full" data-testid="pricing-sessions-table">
            <thead>
              <tr className="bg-gray-100 border-b">
                <th className="text-left px-2 py-2 font-semibold text-gray-700 min-w-[180px] sticky left-0 bg-gray-100 z-10">Session</th>
                <th className="px-1 py-2 font-semibold text-gray-600 w-12">Show</th>
                <th className="px-1 py-2 font-semibold text-blue-600 w-16" title="Enable Online mode"><div className="flex items-center justify-center gap-0.5"><Monitor size={11} /><span className="text-[9px]">Online</span></div></th>
                <th className="px-1 py-2 font-semibold text-teal-600 w-16" title="Enable Offline mode"><div className="flex items-center justify-center gap-0.5"><Wifi size={11} /><span className="text-[9px]">Offline</span></div></th>
                <th className="px-1 py-2 font-semibold text-orange-600 w-16" title="Enable In-Person mode"><div className="flex items-center justify-center gap-0.5"><MapPin size={11} /><span className="text-[9px]">In-Person</span></div></th>
                <th className="px-1 py-2 font-semibold text-purple-600 w-12" title="Show pricing on session card & page">Pricing</th>
                <th className="px-1 py-2 font-semibold text-emerald-700 w-14" title="Client chooses INR amount at checkout">Pay wish</th>
                <th className="px-1 py-2 font-semibold text-emerald-600 min-w-[62px]" title="Minimum INR when pay-as-you-wish is on">Min ₹</th>
                <th className="px-1 py-2 font-semibold text-emerald-500 min-w-[62px]" title="Suggested INR (optional)">Sugg ₹</th>
                <th className="px-1 py-2 font-semibold text-blue-700 min-w-[70px]">AED</th>
                <th className="px-1 py-2 font-semibold text-green-700 min-w-[70px]">INR</th>
                <th className="px-1 py-2 font-semibold text-purple-700 min-w-[70px]">USD</th>
                <th className="px-1 py-2 font-semibold text-amber-700 min-w-[62px]">EB AED</th>
                <th className="px-1 py-2 font-semibold text-amber-600 min-w-[62px]">EB INR</th>
                <th className="px-1 py-2 font-semibold text-amber-500 min-w-[62px]">EB USD</th>
                <th className="px-1 py-2 font-semibold text-amber-800 min-w-[108px]">EB Date</th>
                <th className="px-1 py-2 font-semibold text-amber-800 min-w-[88px]">EB Time</th>
                <th className="px-1 py-2 font-semibold text-amber-700 min-w-[72px]">EB Badge</th>
                <th className="px-1 py-2 font-semibold text-blue-500 min-w-[70px]">Offer AED</th>
                <th className="px-1 py-2 font-semibold text-green-500 min-w-[70px]">Offer INR</th>
                <th className="px-1 py-2 font-semibold text-purple-500 min-w-[70px]">Offer USD</th>
                <th className="px-1 py-2 font-semibold text-red-600 min-w-[90px]">Offer Badge</th>
              </tr>
            </thead>
            <tbody>
              {sessions.filter(s => s.title && s.title.trim() !== '').map((s, idx) => {
                const i = sessions.findIndex(x => x.id === s.id);
                return (
                <tr key={s.id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-yellow-50/30`} data-testid={`pricing-session-${s.id}`}>
                  <td className="px-2 py-1.5 font-medium text-gray-900 sticky left-0 bg-inherit z-10">
                    <div className="font-medium min-w-[180px]" title={s.title}>{s.title}</div>
                  </td>
                  <td className="px-1 py-1 text-center"><Switch checked={s.visible !== false} onCheckedChange={v => updateSession(i, 'visible', v)} /></td>
                  <td className="px-1 py-1 text-center"><Switch checked={s.enable_online !== false} onCheckedChange={v => updateSession(i, 'enable_online', v)} /></td>
                  <td className="px-1 py-1 text-center"><Switch checked={s.enable_offline !== false} onCheckedChange={v => updateSession(i, 'enable_offline', v)} /></td>
                  <td className="px-1 py-1 text-center"><Switch checked={s.enable_in_person === true} onCheckedChange={v => updateSession(i, 'enable_in_person', v)} /></td>
                  <td className="px-1 py-1 text-center"><Switch checked={s.show_pricing !== false} onCheckedChange={v => updateSession(i, 'show_pricing', v)} /></td>
                  <td className="px-1 py-1 text-center"><Switch checked={!!s.pay_as_you_wish} onCheckedChange={v => updateSession(i, 'pay_as_you_wish', v)} /></td>
                  <td className="px-1 py-1"><Cell value={s.pay_as_you_wish_minimum_inr ?? 450} onChange={v => updateSession(i, 'pay_as_you_wish_minimum_inr', v)} className={s.pay_as_you_wish ? '' : 'opacity-40'} /></td>
                  <td className="px-1 py-1"><Cell value={s.pay_as_you_wish_suggested_inr ?? 0} onChange={v => updateSession(i, 'pay_as_you_wish_suggested_inr', v)} className={s.pay_as_you_wish ? '' : 'opacity-40'} /></td>
                  <td className="px-1 py-1"><Cell value={s.price_aed} onChange={v => updateSession(i, 'price_aed', v)} /></td>
                  <td className="px-1 py-1"><Cell value={s.price_inr} onChange={v => updateSession(i, 'price_inr', v)} /></td>
                  <td className="px-1 py-1"><Cell value={s.price_usd} onChange={v => updateSession(i, 'price_usd', v)} /></td>
                  <PricingOfferCells row={s} timeZone={DEFAULT_HUB_TIME_ZONE} onUpdate={(field, value) => updateSession(i, field, value)} />
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={saveAll} disabled={saving} className="bg-[#D4AF37] hover:bg-[#b8962e]" data-testid="pricing-hub-save-bottom">
          <Save size={14} className="mr-1" />{saving ? 'Saving...' : 'Save All Pricing'}
        </Button>
      </div>
    </div>
  );
};

export default PricingHubTab;
